/**
 * Admin schemas for conversations list (Story 3.10).
 *
 * Source of truth: `database-schema.md §4.7` (conversations) joined with
 * §4.3 (customers) for `customer_name`/`customer_phone`, §4.2 (sellers) via
 * `meetings.seller_id`, §4.6 (meetings) for `scheduled_at`.
 *
 * **Why no audit on read:** AC #3 — list is a read-only projection across
 * many rows; LGPD policy forbids audit-on-GET for redaction reasons.
 *
 * **Phone redaction:** AC #4/Dev Notes — the API masks `customer_phone` to
 * the last 4 digits (`+55 ** *** ****1234`) BEFORE serialization. The schema
 * therefore declares the field as a plain string with no E.164 validator —
 * it is intentionally NOT a `Phone` (which requires `^\+\d{10,15}$`).
 *
 * **Filter semantics:**
 *   - `status`: comma-separated list of conversation_status values, e.g.
 *     `awaiting_response,active`. Empty/absent → no status filter.
 *   - `seller_id`: filters by `meetings.seller_id`. Conversations without a
 *     linked meeting are excluded when this filter is present.
 *   - `from` / `to`: ISO date-time bounds against `conversations.created_at`.
 *   - `customer_phone`: substring match against the original (un-masked)
 *     `customers.phone` (digits only, server-side). Useful for the operator
 *     who knows the suffix but not the full number.
 *   - `search`: case-insensitive substring against `customers.name` OR
 *     `customers.phone` (digits-only normalized).
 */
import { Type } from '@sinclair/typebox';
/**
 * All values exposed by the `conversation_status` enum
 * (database-schema.md §3 + db/schema.ts).
 */
export const CONVERSATION_STATUSES = [
    'scheduled',
    'awaiting_response',
    'awaiting_qualification',
    // Story 5.3 — qualified leads transition to `awaiting_slots` after the
    // qualification handler enqueues a `scheduling-queue` job. Story 5.5 will
    // pick up from this status to offer 3 candidate slots.
    'awaiting_slots',
    // Story 5.5 — agent offered 3 slots and is awaiting the customer's pick.
    // The dispatcher routes inbound messages to `handleSlotChoiceInbound`.
    'awaiting_slot_choice',
    // Story 5.5 — SellerDistributor returned null OR the seller's calendar
    // returned 0 free slots. The conversation stalls here until the manager
    // reopens capacity. Distinct from `human_handoff` (Story 3.12) which means
    // "agent stalled, manager assumes conversation".
    'awaiting_human_followup',
    'active',
    'confirmed',
    'cancelled',
    'rescheduled',
    'human_handoff',
    'cold',
    'declined',
    'attended',
    'no_show',
    'closed',
    'error',
];
export const ConversationStatusSchema = Type.Union(CONVERSATION_STATUSES.map((v) => Type.Literal(v)));
/**
 * Subset of intent_type values the UI surfaces as the "last detected intent"
 * column. Mirrors db/schema.ts §3 intent_type. Nullable when no inbound
 * message has been classified yet.
 */
export const INTENT_TYPES = [
    'confirma',
    'cancela',
    'reagenda',
    'qualificacao_resposta',
    'qualificado',
    'desqualificado',
    'pede_humano',
    'opt_out',
    'off_topic',
    'saudacao',
    'duvida',
    'desconhecido',
];
export const IntentTypeSchema = Type.Union(INTENT_TYPES.map((v) => Type.Literal(v)));
/**
 * `ConversationListItem` — the read-side projection returned by the list
 * endpoint. All fields are optional/nullable except `id`, `customerPhone`
 * (already masked), `status`, `createdAt`.
 */
export const ConversationListItem = Type.Object({
    id: Type.String({ format: 'uuid' }),
    /** Story 12.6 (Epic E12) — the customer/lead this conversation belongs to;
     * needed by the admin carteira-reassignment tool, which acts on the
     * customer, not the conversation. */
    customerId: Type.String({ format: 'uuid' }),
    customerName: Type.Union([Type.String(), Type.Null()]),
    /**
     * Masked phone — last 4 digits visible, e.g. `+55 ** *** ****1234`.
     * NOT validated as E.164 because masking destroys the format.
     */
    customerPhone: Type.String(),
    sellerName: Type.Union([Type.String(), Type.Null()]),
    /**
     * Campaign name (2026-07-15) — which campaign/funnel this conversation
     * came from. Rendered as a tag/badge in the list so the operator can tell
     * "Lead Novo — Comandor + RCC" apart from "Lead Novo — CashMiles" apart
     * from "Confirmação Padrão" at a glance.
     */
    campaignName: Type.Union([Type.String(), Type.Null()]),
    /**
     * Story 12.10 (Epic E12) — name of the SDR who owns this lead's carteira,
     * same pattern as `sellerName` above. `null` when the lead has no
     * carteira owner.
     */
    sdrName: Type.Union([Type.String(), Type.Null()]),
    status: ConversationStatusSchema,
    /**
     * 2026-07-21 — `status` intentionally stays `human_handoff` after a
     * release (Story 6.1 Decision #4); this field is the one that reflects
     * whether the AI is CURRENTLY silenced. The list UI uses it to avoid
     * showing a stale "Handoff humano" badge for a conversation that was
     * already released back to the AI.
     */
    agentSilenced: Type.Boolean(),
    /**
     * 2026-07-24 (user request) — non-null when an operator pinned this
     * conversation so it sorts first in the listing, regardless of activity.
     */
    pinnedAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    scheduledAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    lastMessageAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    lastInboundIntent: Type.Union([IntentTypeSchema, Type.Null()]),
    createdAt: Type.String({ format: 'date-time' }),
    /**
     * Story 9.7 — the combined time of a PENDING customer-callback request
     * (Story 9.5's `scheduled_dispatches`, `executed=false AND cancelled=
     * false`), or `null` when there is none. Derived live from the source
     * table on every read (never cached/duplicated onto `conversations`) —
     * same lesson as the Story 8.3 stale-handoff-badge bug this indicator
     * exists to avoid repeating.
     */
    pendingCallbackAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
});
/**
 * Query string for `GET /admin/conversations`. All filters optional.
 * `page`/`perPage` are **strings** because Fastify query coercion does not
 * convert numerics by default. The handler parses + clamps them.
 */
export const ConversationListQuery = Type.Object({
    status: Type.Optional(Type.String()),
    /** 'true' | 'false' — see `ConversationListFilters.agentSilenced` in conversations.repo.ts for why this exists separately from `status`. */
    agentSilenced: Type.Optional(Type.String()),
    sellerId: Type.Optional(Type.String({ format: 'uuid' })),
    /** Story 12.4 (Epic E12) — filter by carteira owner (`customers.assigned_sdr_id`). Forced server-side for a `role='sdr'` session; optional for admin/manager. */
    sdrId: Type.Optional(Type.String({ format: 'uuid' })),
    /** Story 10.3 (2026-07-28, user request) — filter conversations by their own campaign. */
    campaignId: Type.Optional(Type.String({ format: 'uuid' })),
    dateFrom: Type.Optional(Type.String({ format: 'date-time' })),
    dateTo: Type.Optional(Type.String({ format: 'date-time' })),
    customerPhone: Type.Optional(Type.String()),
    search: Type.Optional(Type.String()),
    page: Type.Optional(Type.String()),
    perPage: Type.Optional(Type.String()),
});
export const ConversationListResponse = Type.Object({
    items: Type.Array(ConversationListItem),
    total: Type.Integer({ minimum: 0 }),
    page: Type.Integer({ minimum: 1 }),
    perPage: Type.Integer({ minimum: 1 }),
});
/**
 * Default + max page size — kept here so backend + frontend share the
 * contract.
 */
export const CONVERSATIONS_DEFAULT_PAGE_SIZE = 20;
export const CONVERSATIONS_MAX_PAGE_SIZE = 100;
/**
 * Mask a phone for LGPD redaction. Keeps the last 4 digits visible and
 * replaces the rest with `*`.
 *
 * Examples:
 *   `+5511996811716` → `+55**********1716`
 *   `5511996811716`  → `*********1716`
 *   `1716`           → `1716` (too short to mask)
 *
 * Pure helper — used by both backend route projection and frontend display
 * sanity check.
 */
export function maskPhone(phone) {
    if (typeof phone !== 'string' || phone.length === 0)
        return '';
    if (phone.length <= 4)
        return phone;
    const suffix = phone.slice(-4);
    // Preserve leading `+` if present (E.164).
    if (phone.startsWith('+')) {
        const stars = '*'.repeat(phone.length - 5);
        return `+${stars}${suffix}`;
    }
    const stars = '*'.repeat(phone.length - 4);
    return `${stars}${suffix}`;
}
// ============================================================================
// Story 3.11 — Conversation Detail page
// ============================================================================
/**
 * Sentiment + message-direction + message-type schemas, mirrored inline so the
 * admin schema package stays self-contained (no `@sdr-agend/schemas` cycle).
 */
export const SENTIMENT_TYPES = ['positive', 'neutral', 'negative'];
export const SentimentTypeSchema = Type.Union(SENTIMENT_TYPES.map((s) => Type.Literal(s)));
export const MESSAGE_DIRECTIONS = ['inbound', 'outbound'];
export const MessageDirectionSchema = Type.Union(MESSAGE_DIRECTIONS.map((d) => Type.Literal(d)));
export const MESSAGE_TYPES = ['text', 'template', 'image', 'audio', 'document', 'interactive'];
export const MessageTypeSchema = Type.Union(MESSAGE_TYPES.map((t) => Type.Literal(t)));
/**
 * Top-level conversation card — thinner than the full Drizzle row so the
 * wire payload stays focused on what the detail header renders.
 */
export const ConversationSummary = Type.Object({
    id: Type.String({ format: 'uuid' }),
    customerId: Type.String({ format: 'uuid' }),
    campaignId: Type.String({ format: 'uuid' }),
    /**
     * Human-readable campaign name (2026-07-15) — lets the operator see at a
     * glance which campaign/funnel a conversation came from, especially now
     * that multiple `lead_new` campaigns run concurrently. `null` only if the
     * joined `campaigns` row was somehow removed (should not happen — FK is
     * `onDelete: 'restrict'`).
     */
    campaignName: Type.Union([Type.String(), Type.Null()]),
    meetingId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    status: ConversationStatusSchema,
    agentSilenced: Type.Boolean(),
    /** 2026-07-24 — see `ConversationListItem.pinnedAt` for the full contract. */
    pinnedAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    reactivationAttempts: Type.Integer({ minimum: 0 }),
    rescheduleCount: Type.Integer({ minimum: 0 }),
    lastMessageAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    lastInboundAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    lastInboundIntent: Type.Union([IntentTypeSchema, Type.Null()]),
    lastInboundSentiment: Type.Union([SentimentTypeSchema, Type.Null()]),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
    /** Story 9.7 — see `ConversationListItem.pendingCallbackAt` for the full contract. */
    pendingCallbackAt: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
});
/**
 * Customer card. `phoneMasked` is the ONLY phone shape exposed by the
 * detail endpoint — full E.164 never reaches this schema.
 */
export const CustomerSummary = Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.Union([Type.String(), Type.Null()]),
    phoneMasked: Type.String(),
    email: Type.Union([Type.String(), Type.Null()]),
    optedOut: Type.Boolean(),
});
/** Seller card — optional because conversations may pre-date any meeting. */
export const SellerSummary = Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String(),
    calendarId: Type.Union([Type.String(), Type.Null()]),
});
/** Meeting card — optional for lead-new conversations (no calendar event yet). */
export const MeetingSummary = Type.Object({
    id: Type.String({ format: 'uuid' }),
    scheduledAt: Type.String({ format: 'date-time' }),
    durationMin: Type.Integer({ minimum: 1, maximum: 480 }),
    calendarEventId: Type.String(),
    source: Type.String(),
    outcome: Type.String(),
});
/**
 * Message timeline entry. `text` is `null` for purely media messages;
 * `mediaUrl` is reserved for future media support.
 */
export const MessageTimelineEntry = Type.Object({
    kind: Type.Literal('message'),
    id: Type.String({ format: 'uuid' }),
    occurredAt: Type.String({ format: 'date-time' }),
    direction: MessageDirectionSchema,
    type: MessageTypeSchema,
    text: Type.Union([Type.String(), Type.Null()]),
    mediaUrl: Type.Union([Type.String(), Type.Null()]),
    intent: Type.Union([IntentTypeSchema, Type.Null()]),
    sentiment: Type.Union([SentimentTypeSchema, Type.Null()]),
    templateId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
});
/**
 * Audit timeline entry tied to the conversation. `payload` is forwarded
 * as-is from `audit_log.payload` JSONB; the audit writers (Stories 1.7,
 * 2.2, 3.4, 3.5) already redact PII at insert time.
 */
export const AuditTimelineEntry = Type.Object({
    kind: Type.Literal('audit'),
    id: Type.String({ format: 'uuid' }),
    occurredAt: Type.String({ format: 'date-time' }),
    action: Type.String(),
    payload: Type.Record(Type.String(), Type.Unknown()),
});
export const TimelineEntry = Type.Union([MessageTimelineEntry, AuditTimelineEntry]);
export const ConversationDetailResponse = Type.Object({
    conversation: ConversationSummary,
    customer: CustomerSummary,
    seller: Type.Union([SellerSummary, Type.Null()]),
    meeting: Type.Union([MeetingSummary, Type.Null()]),
    timeline: Type.Array(TimelineEntry),
});
//# sourceMappingURL=conversations.js.map